/** Design primitives — Fortune-500 blueprint Part 3.2.
 *
 *  One import gives you the entire kit:
 *    import { Button, Card, StatTile, ConfidenceBadge, ... } from "@/design";
 *
 *  All primitives read from /design/tokens.js. They support a `tone` prop
 *  ("dark" | "light") so the same component renders correctly on both the
 *  consumer landing surfaces and the broadcast/professional dashboards.
 */
import { useState, useRef, useEffect, useCallback, createContext, useContext } from "react";
import { color, type, radius, space, elevation, motion, transition, font } from "./tokens";

// ── tone helper ────────────────────────────────────────────────────────────
function toneVars(tone) {
  return tone === "dark"
    ? { bg: color.navy900, surface: color.navy800, surfaceAlt: color.navy700, line: color.line, text: "#fff", muted: color.slate400, dim: "#475569" }
    : { bg: color.paper, surface: color.white, surfaceAlt: color.paper, line: color.border, text: color.ink, muted: color.slate500, dim: "#CBD5E1" };
}

// ── Button ─────────────────────────────────────────────────────────────────
export function Button({
  variant = "primary", size = "md", loading, disabled, leftIcon, rightIcon,
  children, audience, fullWidth, "data-testid": testid, onClick, type: htmlType = "button", style: extra,
}) {
  const isDisabled = disabled || loading;
  const sz = { sm: { h: 32, px: 12, fs: 12 }, md: { h: 40, px: 16, fs: 13 }, lg: { h: 48, px: 22, fs: 15 } }[size];
  const accent = audience ? color.audience[audience] : color.cyan600;
  const palette = {
    primary:   { bg: accent, fg: "#fff", border: accent, hov: "#0EA5C0" },
    secondary: { bg: "transparent", fg: accent, border: accent, hov: accent + "11" },
    ghost:     { bg: "transparent", fg: color.slate400, border: "transparent", hov: color.line + "55" },
    danger:    { bg: color.red500, fg: "#fff", border: color.red500, hov: "#DC2626" },
  }[variant];
  return (
    <button
      data-testid={testid}
      type={htmlType}
      disabled={isDisabled}
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
        height: sz.h, padding: `0 ${sz.px}px`, fontSize: sz.fs, fontWeight: 600,
        fontFamily: font.sans, letterSpacing: "0.01em",
        background: palette.bg, color: palette.fg, border: `1.5px solid ${palette.border}`,
        borderRadius: radius.md, cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.55 : 1, width: fullWidth ? "100%" : "auto",
        transition: transition(["background", "border-color", "color", "transform"]),
        ...extra,
      }}
      onMouseEnter={(e) => { if (!isDisabled) e.currentTarget.style.background = palette.hov; }}
      onMouseLeave={(e) => { if (!isDisabled) e.currentTarget.style.background = palette.bg; }}
    >
      {loading ? <Spinner size={sz.fs + 2} color={palette.fg} /> : leftIcon}
      <span>{children}</span>
      {!loading && rightIcon}
    </button>
  );
}

function Spinner({ size = 14, color: c = "#fff" }) {
  return (
    <span aria-label="loading" style={{
      width: size, height: size, borderRadius: "50%",
      border: `2px solid ${c}55`, borderTopColor: c,
      display: "inline-block", animation: "ws-spin 0.7s linear infinite",
    }} />
  );
}

// ── Card ───────────────────────────────────────────────────────────────────
export function Card({ tone = "light", interactive, children, "data-testid": tid, style: extra, onClick }) {
  const v = toneVars(tone);
  return (
    <div data-testid={tid} onClick={onClick}
      style={{
        background: v.surface, border: `1px solid ${v.line}`,
        borderRadius: radius.lg, boxShadow: tone === "dark" ? elevation.e1 : elevation.e1,
        transition: transition(["transform", "box-shadow", "border-color"]),
        cursor: interactive || onClick ? "pointer" : "default",
        ...extra,
      }}
      onMouseEnter={(e) => {
        if (interactive || onClick) {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = elevation.e2;
        }
      }}
      onMouseLeave={(e) => {
        if (interactive || onClick) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = elevation.e1;
        }
      }}>
      {children}
    </div>
  );
}

// ── StatTile (label + big number + optional delta + confidence slot) ──────
export function StatTile({ label, value, delta, deltaSuffix = "%", confidence, accent, tone = "light", sub, "data-testid": tid }) {
  const v = toneVars(tone);
  const a = accent || color.cyan600;
  const deltaUp = typeof delta === "number" && delta > 0;
  const deltaDown = typeof delta === "number" && delta < 0;
  return (
    <div data-testid={tid} style={{ background: v.surface, border: `1px solid ${v.line}`, borderRadius: radius.lg, padding: `${space[5]}px ${space[6]}px`, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${a}, transparent)` }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: space[2] }}>
        <div style={{ ...type.micro, color: v.muted }}>{label}</div>
        {confidence && <ConfidenceBadge source={confidence} size="xs" tone={tone} />}
      </div>
      <div style={{ ...type.number, fontSize: 28, lineHeight: 1.05, color: v.text }}>{value}</div>
      {(sub || delta != null) && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: space[2] }}>
          {delta != null && (
            <span style={{
              ...type.micro, padding: `2px 6px`, borderRadius: radius.sm,
              background: deltaUp ? color.green500 + "22" : deltaDown ? color.red500 + "22" : color.slate400 + "22",
              color: deltaUp ? color.green500 : deltaDown ? color.red500 : color.slate400,
            }}>
              {deltaUp ? "↑" : deltaDown ? "↓" : "·"} {Math.abs(delta)}{deltaSuffix}
            </span>
          )}
          {sub && <span style={{ ...type.small, color: v.muted, fontSize: 11 }}>{sub}</span>}
        </div>
      )}
    </div>
  );
}

// ── ConfidenceBadge (with popover explainer) ──────────────────────────────
// F-115 — label + helper text are now fetched from `@/lib/labels` (canonical
// source). We keep the local CONFIDENCE_DESC table only for the two Phase-2
// values (VERIFIED, SOCIAL) that don't have a data-source counterpart yet.
import { labelFor } from "@/lib/labels";
const PHASE2_DESC = {
  VERIFIED: { title: "Verified", body: "Two or more independent signals (stream + panel + social) agree within 15%. The strongest label we issue." },
  SOCIAL:   { title: "Social cross-check", body: "Listener estimate validated against YouTube + Facebook Live concurrent viewer counts. Phase-2 source." },
};
export function ConfidenceBadge({ source = "MODEL", size = "sm", tone = "dark", "data-testid": tid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const code = (source || "MODEL").toUpperCase();
  const c = color.confidence[code] || color.confidence.MODEL;
  // Prefer the canonical labels dict; fall back to Phase-2 additions.
  const row = labelFor(code);
  const desc = PHASE2_DESC[code] || { title: row.label, body: row.long || row.helper };
  const v = toneVars(tone);
  const sz = { xs: { fs: 8, py: "1px", px: "5px" }, sm: { fs: 9, py: "2px", px: "6px" }, md: { fs: 10, py: "3px", px: "8px" } }[size];
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button data-testid={tid || `confidence-${code.toLowerCase()}`}
        type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }}
        style={{ background: c + "22", color: c, border: `1px solid ${c}55`,
          fontSize: sz.fs, fontWeight: 800, padding: `${sz.py} ${sz.px}`,
          borderRadius: radius.sm, letterSpacing: "0.08em", cursor: "help",
          fontFamily: font.sans }}>
        {code}
      </button>
      {open && (
        <div data-testid="confidence-popover" role="tooltip" style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50,
          background: v.surface, border: `1px solid ${v.line}`,
          borderRadius: radius.md, padding: `${space[4]}px ${space[5]}px`, width: 240,
          boxShadow: elevation.e3, color: v.text }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />
            <strong style={{ fontSize: 13 }}>{desc.title}</strong>
          </div>
          <p style={{ ...type.small, color: v.muted, margin: 0, fontSize: 12 }}>{desc.body}</p>
          <a href="/methodology" data-testid="confidence-methodology-link"
            style={{ ...type.small, color: c, textDecoration: "none", fontSize: 11, fontWeight: 600, display: "inline-block", marginTop: 6 }}>
            Read the methodology →
          </a>
        </div>
      )}
    </span>
  );
}

// ── Pill / Tag ─────────────────────────────────────────────────────────────
export function Pill({ children, variant = "neutral", tone = "light", "data-testid": tid }) {
  const palette = {
    neutral: { bg: color.slate400 + "22", fg: color.slate500 },
    accent:  { bg: color.cyan600 + "22", fg: color.cyan600 },
    success: { bg: color.green500 + "22", fg: color.green500 },
    warning: { bg: color.amber500 + "22", fg: color.amber500 },
    danger:  { bg: color.red500 + "22", fg: color.red500 },
    phase2:  { bg: color.amber500 + "22", fg: color.amber500 },
  }[variant] || { bg: color.slate400 + "22", fg: color.slate500 };
  return (
    <span data-testid={tid} style={{ background: palette.bg, color: palette.fg, ...type.micro, padding: "2px 7px", borderRadius: radius.pill, fontSize: 9 }}>
      {children}
    </span>
  );
}

// ── PageHeader (title + subtitle + actions slot) ──────────────────────────
export function PageHeader({ title, subtitle, eyebrow, actions, tone = "light", "data-testid": tid }) {
  const v = toneVars(tone);
  return (
    <header data-testid={tid} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, padding: `${space[5]}px 0 ${space[5]}px`, borderBottom: `1px solid ${v.line}`, marginBottom: space[7], flexWrap: "wrap" }}>
      <div>
        {eyebrow && <div style={{ ...type.micro, color: v.muted, marginBottom: 4 }}>{eyebrow}</div>}
        <h1 style={{ ...type.h1, color: v.text, margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ ...type.body, color: v.muted, margin: "6px 0 0", maxWidth: 640 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>{actions}</div>}
    </header>
  );
}

// ── SectionHeader (lighter than PageHeader, used inside tabs) ─────────────
export function SectionHeader({ title, subtitle, action, tone = "light" }) {
  const v = toneVars(tone);
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: space[4], flexWrap: "wrap" }}>
      <div>
        <h3 style={{ ...type.h3, color: v.text, margin: 0 }}>{title}</h3>
        {subtitle && <p style={{ ...type.small, color: v.muted, margin: "2px 0 0" }}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

// ── KpiRow — grid of StatTiles with responsive auto-fit ───────────────────
export function KpiRow({ children, minTileWidth = 200 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${minTileWidth}px, 1fr))`, gap: space[4] }}>
      {children}
    </div>
  );
}

// ── Skeleton ───────────────────────────────────────────────────────────────
export function Skeleton({ variant = "line", width = "100%", height, tone = "light", "data-testid": tid }) {
  const v = toneVars(tone);
  const h = height || (variant === "block" ? 80 : variant === "card" ? 140 : 14);
  return (
    <div data-testid={tid} style={{
      width, height: h, borderRadius: variant === "card" ? radius.lg : radius.sm,
      background: `linear-gradient(90deg, ${v.line} 0%, ${v.line}88 50%, ${v.line} 100%)`,
      backgroundSize: "200% 100%", animation: "ws-shimmer 1.4s ease-in-out infinite",
    }} />
  );
}

// ── EmptyState ─────────────────────────────────────────────────────────────
export function EmptyState({ title, helper, action, icon, tone = "light", "data-testid": tid }) {
  const v = toneVars(tone);
  return (
    <div data-testid={tid} style={{ textAlign: "center", padding: `${space[8]}px ${space[5]}px`, background: v.surface, border: `1px dashed ${v.line}`, borderRadius: radius.lg }}>
      <div style={{ marginBottom: space[3], display: "flex", justifyContent: "center" }}>
        {icon || <Waveform width={80} height={36} tone={tone} muted />}
      </div>
      <h3 style={{ ...type.h3, color: v.text, margin: 0, fontFamily: font.sans, fontWeight: 600 }}>{title}</h3>
      {helper && <p style={{ ...type.small, color: v.muted, marginTop: 6, maxWidth: 400, marginLeft: "auto", marginRight: "auto" }}>{helper}</p>}
      {action && <div style={{ marginTop: space[5] }}>{action}</div>}
    </div>
  );
}

// ── Tooltip (controlled hover bubble) ─────────────────────────────────────
export function Tooltip({ children, label, tone = "dark" }) {
  const [show, setShow] = useState(false);
  const v = toneVars(tone);
  return (
    <span style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <span role="tooltip" style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
          background: v.surface, border: `1px solid ${v.line}`, color: v.text,
          ...type.small, fontSize: 11, padding: "6px 10px", borderRadius: radius.sm, boxShadow: elevation.e2, whiteSpace: "nowrap", zIndex: 60 }}>
          {label}
        </span>
      )}
    </span>
  );
}

// ── PriceTag (GH₵ headline + USD secondary) ───────────────────────────────
export function PriceTag({ ghs, usd, suffix = "/mo", tone = "light" }) {
  const v = toneVars(tone);
  if (!ghs) return <span style={{ ...type.h2, color: color.green500, fontFamily: font.sans }}>Free</span>;
  return (
    <div>
      <div style={{ ...type.number, fontSize: 32, color: v.text, lineHeight: 1.1 }}>
        GH₵ {Math.round(ghs).toLocaleString("en-GH")}
        <span style={{ ...type.small, color: v.muted, fontWeight: 500, marginLeft: 4 }}>{suffix}</span>
      </div>
      {usd != null && (
        <div style={{ ...type.number, fontSize: 12, color: v.muted, marginTop: 3 }}>
          ≈ ${Math.round(usd).toLocaleString("en-US")} {suffix}
        </div>
      )}
    </div>
  );
}

// ── Tabs ───────────────────────────────────────────────────────────────────
export function Tabs({ tabs, value, onChange, tone = "light", audience }) {
  const v = toneVars(tone);
  const a = audience ? color.audience[audience] : color.cyan600;
  return (
    <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${v.line}`, overflowX: "auto" }}>
      {tabs.map((t) => {
        const active = t.id === value;
        return (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => onChange(t.id)}
            style={{
              padding: `${space[3]}px ${space[5]}px`, background: "none",
              border: "none", borderBottom: `2px solid ${active ? a : "transparent"}`,
              color: active ? v.text : v.muted, ...type.body, fontWeight: active ? 700 : 500, fontSize: 13,
              cursor: "pointer", transition: transition(["color", "border-color"]),
              whiteSpace: "nowrap",
            }}>
            {t.label}
            {t.badge != null && (
              <span style={{ marginLeft: 6, background: active ? a : v.line, color: active ? "#fff" : v.muted, fontSize: 10, padding: "1px 6px", borderRadius: radius.pill, fontWeight: 700 }}>
                {t.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Table (sortable, sticky header, zebra) ────────────────────────────────
export function Table({ columns, rows, tone = "light", density = "comfortable", onRowClick, "data-testid": tid }) {
  const v = toneVars(tone);
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const pad = density === "compact" ? "8px 10px" : "12px 14px";

  const sortedRows = sort.key ? [...rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    if (x === y) return 0;
    if (x == null) return 1; if (y == null) return -1;
    if (typeof x === "number") return (sort.dir === "asc" ? x - y : y - x);
    return sort.dir === "asc" ? String(x).localeCompare(String(y)) : String(y).localeCompare(String(x));
  }) : rows;

  return (
    <div data-testid={tid} style={{ overflowX: "auto", background: v.surface, border: `1px solid ${v.line}`, borderRadius: radius.lg }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: font.sans, fontSize: 13 }}>
        <thead style={{ position: "sticky", top: 0, background: v.surfaceAlt, zIndex: 1 }}>
          <tr>
            {columns.map((c) => (
              <th key={c.key} onClick={() => c.sortable && setSort((s) => ({ key: c.key, dir: s.key === c.key && s.dir === "desc" ? "asc" : "desc" }))}
                style={{ ...type.micro, color: v.muted, textAlign: c.align || "left", padding: pad,
                  borderBottom: `1px solid ${v.line}`, cursor: c.sortable ? "pointer" : "default",
                  userSelect: "none", whiteSpace: "nowrap" }}>
                {c.label}{c.sortable && sort.key === c.key && (sort.dir === "asc" ? " ▲" : " ▼")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r, i) => (
            <tr key={r.id || i} data-testid={`table-row-${i}`} onClick={() => onRowClick?.(r)}
              style={{ background: i % 2 ? "transparent" : v.surfaceAlt + "55", cursor: onRowClick ? "pointer" : "default",
                transition: transition(["background"]) }}
              onMouseEnter={(e) => { if (onRowClick) e.currentTarget.style.background = v.line + "44"; }}
              onMouseLeave={(e) => { if (onRowClick) e.currentTarget.style.background = i % 2 ? "transparent" : v.surfaceAlt + "55"; }}>
              {columns.map((c) => (
                <td key={c.key} style={{ padding: pad, color: v.text, textAlign: c.align || "left", borderBottom: `1px solid ${v.line}55` }}>
                  {c.render ? c.render(r[c.key], r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} style={{ padding: space[6], color: v.muted, textAlign: "center", ...type.small }}>No data yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Toast (sonner already exists — re-export for kit completeness) ────────
import { toast as sonnerToast } from "sonner";
export const toast = sonnerToast;

// ── Waveform — the brand audio motif ──────────────────────────────────────
export function Waveform({ width = 120, height = 40, bars = 7, color: c, tone = "dark", muted, "data-testid": tid }) {
  const fill = c || (tone === "dark" ? color.cyan400 : color.cyan600);
  return (
    <svg data-testid={tid} width={width} height={height} viewBox={`0 0 ${bars * 8} 24`}
      aria-hidden="true" style={{ display: "inline-block", opacity: muted ? 0.4 : 1 }}>
      {Array.from({ length: bars }).map((_, i) => {
        const delay = (i * 0.12).toFixed(2);
        const h = 6 + ((i * 37) % 14);
        return (
          <rect key={i} x={i * 8} width={4} y={(24 - h) / 2} height={h} rx={2} fill={fill}
            style={muted ? {} : { transformOrigin: "center", animation: `ws-wave 1.4s ${delay}s ease-in-out infinite` }} />
        );
      })}
    </svg>
  );
}

// ── Global keyframes — injected once ───────────────────────────────────────
let __keyframesInjected = false;
export function ensureKeyframes() {
  if (typeof window === "undefined" || __keyframesInjected) return;
  __keyframesInjected = true;
  const css = `
    @keyframes ws-spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }
    @keyframes ws-shimmer { 0% { background-position: -200% 0;} 100% { background-position: 200% 0;} }
    @keyframes ws-wave { 0%,100% { transform: scaleY(0.55);} 50% { transform: scaleY(1.6);} }
    @keyframes ws-fade-in { from { opacity: 0; transform: translateY(4px);} to { opacity: 1; transform: translateY(0);} }
  `;
  const style = document.createElement("style");
  style.textContent = css;
  document.head.appendChild(style);
}

// ── Toast/Theme Provider (small wrapper for global setup) ─────────────────
const DesignContext = createContext({ tone: "dark" });
export function DesignProvider({ tone = "dark", children }) {
  useEffect(() => { ensureKeyframes(); }, []);
  const value = { tone };
  return <DesignContext.Provider value={value}>{children}</DesignContext.Provider>;
}
export function useDesign() { return useContext(DesignContext); }

// ── re-exports for convenience ────────────────────────────────────────────
export { color, type, radius, space, elevation, motion, transition, font } from "./tokens";
